import camelCaseKeys from 'camelcase-keys';
import cleanDeep from 'clean-deep';
import snakeCaseKeys from 'snakecase-keys';

import { createTransformationStream } from './create-transformation-stream.ts';

/**
 * Fetch wrapper that bridges OpenAI SDK (snake_case) and Cognite AI API (camelCase).
 * Converts request/response between naming conventions and handles streaming transformations.
 *
 * @see https://api-docs.cogheim.net/redoc/#tag/Chat-completions
 */
export async function cogniteAiApiFetch(url: string | URL | Request, init?: RequestInit) {
  let convertedRequestBody: string | undefined;
  let isStreamingRequest = false;

  // Transform request body from OpenAI SDK format (snake_case) to Cognite AI API format (camelCase)
  if (init?.body) {
    const parsedBodyInSnakeCase = JSON.parse(init.body.toString());
    const parsedBodyInCamelCase = camelCaseKeys(parsedBodyInSnakeCase);

    isStreamingRequest = !!parsedBodyInCamelCase.stream;

    // Remove null-valued params (e.g. `parsed`, `parsed_parameters`) that OpenAI SDK sends but Cognite AI API doesn't support
    const parsedBodyInCamelCaseWithoutNulls = cleanDeep(parsedBodyInCamelCase, {
      emptyArrays: false,
      emptyObjects: false,
      emptyStrings: false,
      // biome-ignore lint/style/useNamingConvention: NaN is a standard acronym
      NaNValues: false,
      nullValues: true,
      undefinedValues: true,
    });

    convertedRequestBody = JSON.stringify(parsedBodyInCamelCaseWithoutNulls);
  }

  let overriddenRequestHeaders = {} as HeadersInit;
  let token = '';

  // Strip OpenAI SDK headers (x-stainles*, user-agent) and extract auth token for Cognite AI API format
  if (init?.headers) {
    const headers = new Headers(init.headers);
    for (const [key, value] of headers.entries()) {
      if (key.toLowerCase() === 'authorization') {
        token = value;
      }
      // Filter out OpenAI SDK-specific headers
      if (!key.toLowerCase().startsWith('x-stainles') && !key.toLowerCase().startsWith('user-agent')) {
        overriddenRequestHeaders[key as keyof HeadersInit] = value;
      }
    }
    overriddenRequestHeaders = {
      ...overriddenRequestHeaders,
      authorization: token,
      accept: isStreamingRequest ? 'text/event-stream' : 'application/json',
      'content-type': 'application/json',
    };
  }

  const convertedRequest = {
    ...(init || {}),
    headers: overriddenRequestHeaders,
    body: convertedRequestBody,
  };

  const response = await fetch(url.toString(), convertedRequest);

  // Transform streaming response from camelCase to snake_case for OpenAI SDK compatibility
  if (isStreamingRequest) {
    // Must create new transformation stream to avoid locking the original stream reader
    return new Response(response.body?.pipeThrough(createTransformationStream()), response);
  }

  // Transform non-streaming response from camelCase to snake_case
  const responseDataInCamelCase = await response.json();
  const responseDataInSnakeCase = snakeCaseKeys(responseDataInCamelCase);

  const convertedResponse = new Response(JSON.stringify(responseDataInSnakeCase), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });

  return convertedResponse;
}
